import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  MdDashboard,
  MdPeople,
  MdCalendarToday,
  MdPerson,
  MdLogout,
  MdMenu,
  MdClose,
  MdLocalHospital,
} from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import logo from "../assets/logo1.png";

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED = 65;

const THEME = {
  primary: "#2F6DB4",
  primaryLight: "#5894d9",
  bg: "#E3ECF6",
  border: "#BBD6F5",
  glow: "rgba(47,109,180,0.35)",
};

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: MdDashboard },
  { to: "/patients", label: "My Patients", icon: MdPeople },
  { to: "/duties", label: "My Duties", icon: MdCalendarToday },
  { to: "/profile", label: "My Profile", icon: MdPerson },
];

export default function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const doctorRaw = Cookies.get("doctor");
  const doctor = doctorRaw ? JSON.parse(doctorRaw) : {};

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setIsMobile(w < 640);
      setIsTablet(w >= 640 && w < 1024);
      if (w >= 640 && w < 1024) setSidebarOpen(false);
      if (w >= 1024) setSidebarOpen(true);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  const handleMenuClick = () => {
    if (isMobile) setMobileOpen(!mobileOpen);
    else setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    Cookies.remove("doctor");
    navigate("/login");
  };

  const activeItem = navItems.find((item) => location.pathname.startsWith(item.to));
  const pageTitle = activeItem?.label || "Dashboard";

  const SidebarContent = ({ expanded }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-center px-3 pb-4 border-b border-gray-100 mb-2 ">
        {expanded ? (
          <div className="flex items-center justify-center gap-3 w-full px-2">
          <div>
            <img src={logo} alt="Logo" className=" rounded-lg h-48 " />
          </div>
            {/* <div className="min-w-0">
              <p className="text-gray-800 font-bold text-sm leading-tight m-0">Doctor Portal</p>
              <p className="text-gray-400 text-[10px] uppercase tracking-wider m-0">HMS</p>
            </div> */}
          </div>
        ) : (
          <div className=" rounded-lg flex items-center justify-center">
            <img src={logo} alt="Logo" className=" h-22 w-24 object-contain" />
          </div>
        )}
      </div>

   

      {expanded && (
        <p className="text-[10px] font-bold tracking-[1.2px] uppercase text-gray-400 px-5 pt-2 pb-1 m-0">
          Navigation
        </p>
      )}

      {/* Nav */}
      <nav className="py-1 flex-1">
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.to);
          const Icon = item.icon;
          return (
            <div
              key={item.to}
              onClick={() => {
                navigate(item.to);
                setMobileOpen(false);
              }}
              title={!expanded ? item.label : ""}
              className="relative flex items-center cursor-pointer rounded-xl overflow-hidden mx-2 my-[2px] px-[10px] py-[9px] border transition-all duration-200"
              style={
                isActive
                  ? { background: THEME.bg, borderColor: THEME.border, boxShadow: `0 2px 8px ${THEME.primary}22` }
                  : { background: "transparent", borderColor: "transparent" }
              }
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = "#f8f9fa";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                }
              }}
            >
              <div
                className="flex items-center justify-center rounded-[8px] flex-shrink-0 transition-all duration-200"
                style={{
                  width: 32,
                  height: 32,
                  background: isActive ? THEME.primary : "#f1f3f5",
                  boxShadow: isActive ? `0 4px 14px ${THEME.glow}` : "none",
                  border: isActive ? "none" : "1px solid #e5e7eb",
                }}
              >
                <Icon size={17} style={{ color: isActive ? "#fff" : "#6b7280" }} />
              </div>

              {expanded && (
                <span
                  className="ml-[10px] text-[13px] font-semibold whitespace-nowrap overflow-hidden flex-1"
                  style={{ color: isActive ? THEME.primary : "#4b5563" }}
                >
                  {item.label}
                </span>
              )}

              {isActive && (
                <div
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-[3px] h-[55%] rounded-l"
                  style={{ background: THEME.primary }}
                />
              )}
            </div>
          );
        })}
      </nav>

     
    </div>
  );

  return (
    <div className="flex items-stretch min-h-screen bg-slate-100">
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
        * { font-family: 'Poppins', sans-serif; }
      `}</style>

      {/* Mobile overlay + drawer */}
      {isMobile && (
        <>
          <div
            className="fixed inset-0 z-50 transition-opacity duration-300"
            style={{
              background: "rgba(0,0,0,0.45)",
              opacity: mobileOpen ? 1 : 0,
              pointerEvents: mobileOpen ? "auto" : "none",
              backdropFilter: mobileOpen ? "blur(2px)" : "none",
            }}
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="fixed top-0 left-0 h-full z-[60] bg-white shadow-2xl"
            style={{
              width: DRAWER_WIDTH,
              borderTopRightRadius: 24,
              borderBottomRightRadius: 24,
              transform: mobileOpen ? "translateX(0)" : `translateX(-${DRAWER_WIDTH}px)`,
              transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
            >
              <MdClose size={18} className="text-gray-500" />
            </button>
            <SidebarContent expanded={true} />
          </div>
        </>
      )}

      {/* Desktop drawer */}
      {!isMobile && (
        <aside
          // className="flex-shrink-0 bg-white overflow-hidden transition-[width] duration-300"
          className="flex-shrink-0 bg-white overflow-hidden transition-[width] duration-300 sticky top-0 h-screen"
          style={{
            width: sidebarOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED,
            borderTopRightRadius: 24,
            borderBottomRightRadius: 24,
            boxShadow: "6px 0 32px rgba(0,0,0,0.10)",
          }}
        >
          <SidebarContent expanded={sidebarOpen} />
        </aside>
      )}

      {/* Main */}
      <div className="flex flex-col flex-1 min-w-0" style={{ minHeight: "100vh" }}>
        {/* Topbar */}
        <div className="sticky top-0 z-30 px-2 pt-2 pb-0">
          <div className="rounded-2xl border border-white/70 bg-white/85 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
            <div className="flex items-center min-h-[56px] px-3 gap-2">
              <button
                onClick={handleMenuClick}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-700/[0.07] hover:bg-gray-700/[0.14] transition-colors flex-shrink-0"
              >
                <MdMenu size={20} className="text-gray-700" />
              </button>

              <div className="ml-1 min-w-0 flex-1">
                <p className="text-[14px] sm:text-[15px] font-bold text-gray-700 m-0 leading-tight truncate">
                  {pageTitle}
                </p>
                <p className="text-[10px] sm:text-[11px] text-gray-400 m-0 hidden sm:block">
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>

              {/* Profile menu */}
              <div className="relative flex-shrink-0">
                <div
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-[6px] rounded-full cursor-pointer select-none
                    shadow-[0_2px_12px_rgba(47,109,180,0.25)]
                    hover:shadow-[0_4px_20px_rgba(47,109,180,0.38)] transition-shadow duration-200"
                  style={{ background: `linear-gradient(to right, ${THEME.primary}, ${THEME.primaryLight})` }}
                >
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-white text-[11px] font-semibold">
                      {doctor?.name?.charAt(0)?.toUpperCase() || "D"}
                    </span>
                  </div>
                  <span className="text-[12px] sm:text-[13px] font-semibold text-white max-w-[80px] sm:max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap hidden xs:inline">
                    {doctor?.name || "Doctor"}
                  </span>
                  <IoIosArrowDown
                    className="w-3 h-3 text-white/70 transition-transform duration-300"
                    style={{ transform: menuOpen ? "rotate(-180deg)" : "rotate(0deg)" }}
                  />
                </div>

                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-[9998]" onClick={() => setMenuOpen(false)} />
                    <div
                      className="absolute top-[calc(100%+10px)] right-0 z-[9999]
                        min-w-[180px] sm:min-w-[220px] rounded-2xl overflow-hidden
                        bg-white/[0.97] backdrop-blur-xl
                        border border-gray-700/10
                        shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
                    >
                      <div
                        className="flex items-center gap-2.5 p-3 sm:p-4"
                        style={{ background: `linear-gradient(to right, ${THEME.primary}, ${THEME.primaryLight})` }}
                      >
                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                          <MdPerson className="text-white text-sm sm:text-base" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-[12px] sm:text-[13px] font-bold m-0 truncate">
                            {doctor?.name || "Doctor"}
                          </p>
                          <p className="text-white/60 text-[10px] sm:text-[11px] m-0 truncate">
                            {doctor?.specialization || "Doctor"}
                          </p>
                        </div>
                      </div>

                      <div
                        onClick={handleLogout}
                        className="flex items-center gap-2.5 m-2 sm:m-2.5 px-3 sm:px-3.5 py-2 sm:py-2.5 rounded-xl cursor-pointer
                          bg-gradient-to-r from-red-500 to-red-600
                          text-white font-semibold text-[12px] sm:text-[13px]
                          shadow-[0_3px_12px_rgba(239,68,68,0.3)]
                          hover:-translate-y-px hover:shadow-[0_6px_18px_rgba(239,68,68,0.4)]
                          transition-all duration-200"
                      >
                        <MdLogout size={16} />
                        <span>Sign Out</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="flex-1 p-2 pt-2">
          <div className="bg-white min-h-[calc(100vh-80px)] p-3 sm:p-4 rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}